import { Add } from '@carbon/react/icons';
import { AtomField, AtomizedRowForm } from 'atoms/form-atoms';
import { FormRow, FormSection } from 'components/forms';
import { AddressAttributes, EmailAttributes, PhoneAttributes } from './types';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ComponentType,
  ReactNode,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button, Checkbox, Column, Row } from '@carbon/react';
import { addressRow, emailRow, phoneNumberRow } from './utils';
import { TextField } from './text-field';
import { AddressAutofill as AddressAutofillBase } from '@mapbox/search-js-react';
import type { AddressAutofillProps } from '@mapbox/search-js-react/dist/components/AddressAutofill';
// @mapbox/search-js-react types AddressAutofill's return as `ReactElement | null`,
// which @types/react 18 rejects as a JSX element type; cast to ComponentType.
// The ForwardRefExoticComponent return type no longer overlaps with ComponentType
// under @types/react 18, so bridge through `unknown` while preserving the real props.
const AddressAutofill =
  AddressAutofillBase as unknown as ComponentType<AddressAutofillProps>;
import type { TitleWindow } from 'types/window';
import { TrashRowButton } from 'components/trash-row-button';

declare let window: TitleWindow;

import style from './form.module.scss';

// Only the address list grows into the hundreds (one entity carries ~961), so
// windowing kicks in past this count; short phone/email lists keep the plain map.
const VIRTUALIZE_THRESHOLD = 30;
// Initial guess for one address entry: two Carbon input rows (~168px) plus the
// divider and its 16px gaps (~33px, see `.virtualRow::after`). The real height
// replaces this once measureElement observes the rendered row; the estimate only
// sizes the scrollbar before first paint.
const ADDRESS_ROW_ESTIMATE = 201;

type ContactInfoRow = {
  index: number;
  onDelete: (index: number) => void;
  onPrimaryUpdate: (index: number) => void;
};

type PhoneRow = ContactInfoRow & {
  entry: AtomizedRowForm<PhoneAttributes>;
};

type EmailRow = ContactInfoRow & {
  entry: AtomizedRowForm<EmailAttributes>;
};

type AddressRow = ContactInfoRow & {
  entry: AtomizedRowForm<AddressAttributes>;
};

export type ContactInfoRowsAtom = AtomField<
  AtomizedRowForm<PhoneAttributes | EmailAttributes | AddressAttributes>[]
>;

const PhoneRow = ({ entry, index, onDelete, onPrimaryUpdate }: PhoneRow) => {
  const rowStatus = useAtomValue(entry._rowStatus);
  const [primary, setPrimary] = useAtom(entry.primary);
  const status = rowStatus.value;
  return (
    <>
      <FormRow toTrash={status === 'trash'}>
        <Row>
          <Column md={5} lg={10}>
            <TextField
              id={`phone-number-${index}`}
              labelText="Phone Number (Required)"
              fieldAtom={entry.number}
            />
          </Column>
          <Column md={2} lg={4}>
            <Checkbox
              id={`phone-primary-${index}`}
              labelText={'Primary'}
              className={style.adjustForLabel}
              checked={primary.value}
              onChange={(_evt, { checked }: { checked: boolean }) => {
                setPrimary(checked);
                onPrimaryUpdate(index);
              }}
            />
          </Column>
          <Column md={1} lg={2}>
            <TrashRowButton
              adjustForLabel={true}
              atom={entry._rowStatus}
              index={index}
              onDelete={onDelete}
            />
          </Column>
        </Row>
      </FormRow>
    </>
  );
};

const EmailRow = ({ entry, index, onDelete, onPrimaryUpdate }: EmailRow) => {
  const rowStatus = useAtomValue(entry._rowStatus);
  const [primary, setPrimary] = useAtom(entry.primary);
  const status = rowStatus.value;
  return (
    <>
      <FormRow toTrash={status === 'trash'}>
        <Row>
          <Column md={5} lg={10}>
            <TextField
              id={`email-${index}`}
              labelText="E-mail (Required)"
              fieldAtom={entry.email}
            />
          </Column>
          <Column md={2} lg={4}>
            <Checkbox
              id={`email-primary-${index}`}
              labelText={'Primary'}
              className={style.adjustForLabel}
              checked={primary.value}
              onChange={(_evt, { checked }: { checked: boolean }) => {
                setPrimary(checked);
                onPrimaryUpdate(index);
              }}
            />
          </Column>
          <Column md={1} lg={2}>
            <TrashRowButton
              adjustForLabel={true}
              atom={entry._rowStatus}
              index={index}
              onDelete={onDelete}
            />
          </Column>
        </Row>
      </FormRow>
    </>
  );
};

const mapboxAutocompleteOptions = {
  lenguage: 'en',
  country: 'US',
};

const AddressRow = ({
  entry,
  index,
  onDelete,
  onPrimaryUpdate,
}: AddressRow) => {
  const rowStatus = useAtomValue(entry._rowStatus);
  const [primary, setPrimary] = useAtom(entry.primary);
  const status = rowStatus.value;

  return (
    <FormRow toTrash={status === 'trash'}>
      <Row className={style.rowThirds}>
        <Column>
          <AddressAutofill
            options={mapboxAutocompleteOptions}
            accessToken={window.CONFIG?.mapbox.token || ''}
          >
            <TextField
              id={`address-street-address-${index}`}
              labelText="Street Address"
              fieldAtom={entry.streetAddress}
              placeholder="Type the address, e.g. 123 Main..."
              autoComplete="address-line1"
            />
          </AddressAutofill>
        </Column>
        <Column>
          <TextField
            id={`address-city-${index}`}
            labelText="City"
            fieldAtom={entry.city}
            placeholder="City"
            autoComplete="address-level2"
          />
        </Column>
        <Column>
          <TextField
            id={`address-state-${index}`}
            labelText="State / Region"
            fieldAtom={entry.state}
            placeholder="State / Region"
            autoComplete="address-level1"
          />
        </Column>
      </Row>
      <Row className={style.rowThirds}>
        <Column>
          <TextField
            id={`address-zip-${index}`}
            labelText="Zip Code / Postcode"
            fieldAtom={entry.zipCode}
            placeholder="ZIP / Postcode"
            autoComplete="postal-code"
          />
        </Column>
        <Column>
          <Checkbox
            id={`address-primary-${index}`}
            labelText={'Primary'}
            className={style.adjustForLabel}
            checked={primary.value}
            onChange={(_evt, { checked }: { checked: boolean }) => {
              setPrimary(checked);
              onPrimaryUpdate(index);
            }}
          />
        </Column>
        <Column>
          <TrashRowButton
            adjustForLabel={true}
            atom={entry._rowStatus}
            index={index}
            onDelete={onDelete}
          />
        </Column>
      </Row>
    </FormRow>
  );
};

const ContactInfoRows = <T extends ContactInfoRowsAtom>({
  form,
  title,
  addLabel,
  type,
}: {
  form: T;
  title: string;
  addLabel: string;
  type: 'phone' | 'email' | 'address';
}) => {
  const [rows, setRows] = useAtom(form);

  const onDelete = useCallback(
    (index: number) =>
      setRows((arr) => [...arr.slice(0, index), ...arr.slice(index + 1)]),
    [setRows]
  );

  const updatePrimaryFieldsAtom = useMemo(() => {
    return atom<null, [number], void>(
      () => null,
      (get, set, index) => {
        const rows = get(form);
        rows.value.map((el, i) => {
          if (i !== index) {
            set(el.primary, false);
          }
        });
      }
    );
  }, [form]);

  const rowAdder =
    type === 'phone'
      ? phoneNumberRow
      : type === 'email'
        ? emailRow
        : addressRow;

  const updatePrimaryField = useSetAtom(updatePrimaryFieldsAtom);

  const renderRow = useCallback(
    (
      el: AtomizedRowForm<
        PhoneAttributes | EmailAttributes | AddressAttributes
      >,
      i: number
    ): ReactNode => (
      <FormSection title="" key={`${i}-interest-entry`}>
        {'number' in el && (
          <PhoneRow
            entry={el}
            index={i}
            onDelete={onDelete}
            onPrimaryUpdate={updatePrimaryField}
          />
        )}
        {'email' in el && (
          <EmailRow
            entry={el}
            index={i}
            onDelete={onDelete}
            onPrimaryUpdate={updatePrimaryField}
          />
        )}
        {'streetAddress' in el && (
          <AddressRow
            entry={el}
            index={i}
            onDelete={onDelete}
            onPrimaryUpdate={updatePrimaryField}
          />
        )}
      </FormSection>
    ),
    [onDelete, updatePrimaryField]
  );

  // Windowed rendering for long address lists. The rows live in Jotai atoms (the
  // form store), not in component/DOM state, so a row scrolled out of the window
  // keeps its value and re-mounts unchanged — only the visible window is paid for
  // in the DOM, instead of mounting hundreds of AddressRows (each carrying a Mapbox
  // AddressAutofill session) at once, which locks up the page.
  const rowCount = rows.value.length;
  const virtualizeEnabled =
    type === 'address' && rowCount > VIRTUALIZE_THRESHOLD;

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ADDRESS_ROW_ESTIMATE,
    overscan: 6,
  });

  // A newly-added row is appended at the end, which in the windowed branch is far
  // below the current scroll offset and so isn't rendered at all. Scroll it into
  // view once the append has re-rendered, so "Add New Address" always reveals the
  // new (empty) row to edit.
  const scrollToNewRowOnAppend = useRef(false);
  useEffect(() => {
    if (scrollToNewRowOnAppend.current && virtualizeEnabled && rowCount > 0) {
      virtualizer.scrollToIndex(rowCount - 1);
    }
    scrollToNewRowOnAppend.current = false;
  }, [rowCount, virtualizeEnabled, virtualizer]);

  const onAddRow = useCallback(() => {
    scrollToNewRowOnAppend.current = true;
    setRows((current) => [...current, rowAdder()]);
  }, [setRows, rowAdder]);

  return (
    <FormSection title={title}>
      {virtualizeEnabled ? (
        <div
          ref={parentRef}
          data-testid="virtualized-address-scroll"
          className={style.virtualScroll}
        >
          <div
            className={style.virtualSizer}
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((vi) => (
              <div
                key={vi.index}
                data-index={vi.index}
                ref={virtualizer.measureElement}
                className={style.virtualRow}
                style={{ transform: `translateY(${vi.start}px)` }}
              >
                {renderRow(rows.value[vi.index], vi.index)}
              </div>
            ))}
          </div>
        </div>
      ) : (
        rows.value.map((el, i) => renderRow(el, i))
      )}
      <FormRow>
        <Button renderIcon={Add} kind={'tertiary'} size="md" onClick={onAddRow}>
          {addLabel}
        </Button>
      </FormRow>
    </FormSection>
  );
};

export { ContactInfoRows };
